<template>
  <div class="modal-card up-modal">
    <header class="modal-card-head up-modal-head">
      <div class="up-modal-icon" :class="assignType === 'T' ? 'is-teacher' : 'is-student'">
        <svg v-if="assignType === 'T'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path></svg>
        <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
      </div>
      <div>
        <h2 class="up-modal-title">Assign {{ assignType === 'T' ? 'Teachers' : 'Students' }}</h2>
        <p class="up-modal-sub">Select people from your school to add to this classroom.</p>
      </div>
      <button class="up-modal-close" type="button" @click="$parent.close()" aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
      </header>

    <section class="modal-card-body up-modal-body">
      <div class="tabs">
        <button class="tab" :class="{ 'is-active': assignType === 'T' }" @click="setType('T')">
          <span class="tab-dot" style="--c: #8919e7"></span> Teachers
        </button>
        <button class="tab" :class="{ 'is-active': assignType === 'S' }" @click="setType('S')">
          <span class="tab-dot" style="--c: #31c8ff"></span> Students
        </button>
      </div>

      <div class="search-bar">
        <span class="search-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </span>
        <input v-model="search" type="text" :placeholder="`Search ${assignType === 'T' ? 'teachers' : 'students'} by name or email`" />
          </div>

      <div v-if="isLoading" class="state">
        <div class="spinner"></div>
                        </div>

      <div v-else-if="!filteredUsers.length" class="state">
        <div class="state-emoji">{{ search ? '🔍' : '🪄' }}</div>
        <div class="state-text">
          {{ search ? 'No users match your search' : (availableUsers.length ? 'Everyone is already assigned' : 'No users available to assign') }}
            </div>
          </div>

      <div v-else class="user-list">
        <div class="list-toolbar">
          <label class="check-row">
            <input
              type="checkbox"
              :checked="allFilteredSelected"
              :indeterminate.prop="someFilteredSelected && !allFilteredSelected"
              @change="toggleAllFiltered($event.target.checked)"
            />
            <span class="checkmark"></span>
            <span class="check-label">
              {{ allFilteredSelected ? 'Deselect all' : 'Select all' }}
              <span class="count-pill">{{ filteredUsers.length }}</span>
            </span>
          </label>
          <span class="selected-info" v-if="selectedIds.length">{{ selectedIds.length }} selected</span>
        </div>

        <div class="user-rows">
          <label
            v-for="user in filteredUsers"
            :key="user._id"
            class="user-row"
            :class="{ 'is-selected': isSelected(user) }"
          >
            <input
              type="checkbox"
              :checked="isSelected(user)"
              @change="toggleUser(user, $event.target.checked)"
            />
            <span class="checkmark"></span>
            <span class="user-avatar" :class="assignType === 'T' ? 'is-teacher' : 'is-student'">{{ initials(user.name) }}</span>
            <div class="user-info">
              <div class="user-name">{{ user.name }}</div>
              <div class="user-email">{{ user.email }}</div>
            </div>
          </label>
          </div>
        </div>
      </section>

    <footer class="modal-card-foot up-modal-foot">
      <button class="up-btn up-btn-ghost" type="button" @click="$parent.close()">Cancel</button>
      <button class="up-btn up-btn-primary" :disabled="!selectedIds.length || isSubmitting" @click="proceed()">
        <span v-if="!isSubmitting">
          Assign {{ selectedIds.length || '' }} {{ assignType === 'T' ? 'Teacher' : 'Student' }}{{ selectedIds.length === 1 ? '' : 's' }}
        </span>
        <span v-else>Assigning…</span>
        </button>
      </footer>
    </div>
  </template>
  
  <script>
import { mapActions, mapGetters } from 'vuex'

  export default {
  name: 'assign-classroom',
  props: {
    id: { type: String, required: true },
    assignType: { type: String, default: 'T' },
    existingTeacherIds: { type: Array, default: () => [] },
    existingStudentIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      search: '',
      selectedIds: [],
      // local copy of assignType so user can switch tabs inside the modal
      currentType: this.assignType
    }
  },
  computed: {
    ...mapGetters(['getTeacherToken', 'fetchSchoolTeachers', 'fetchSchoolStudents']),
    sourceUsers() {
      return this.currentType === 'T' ? (this.fetchSchoolTeachers || []) : (this.fetchSchoolStudents || [])
    },
    excludedIds() {
      return this.currentType === 'T' ? this.existingTeacherIds : this.existingStudentIds
    },
    availableUsers() {
      const excluded = new Set(this.excludedIds.map(String))
      return this.sourceUsers.filter(u => !excluded.has(String(u._id)))
    },
    filteredUsers() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.availableUsers
      return this.availableUsers.filter(u => {
        return (u.name || '').toLowerCase().includes(q) ||
               (u.email || '').toLowerCase().includes(q)
      })
    },
    allFilteredSelected() {
      if (!this.filteredUsers.length) return false
      return this.filteredUsers.every(u => this.isSelected(u))
    },
    someFilteredSelected() {
      return this.filteredUsers.some(u => this.isSelected(u))
    }
  },
  watch: {
    assignType(val) {
      this.currentType = val
      this.selectedIds = []
      this.search = ''
      this.loadUsers()
    }
  },
  methods: {
    ...mapActions(['loadSchoolUsers', 'assignClassroom']),
    setType(t) {
      if (this.currentType === t) return
      this.currentType = t
      this.selectedIds = []
      this.search = ''
      this.loadUsers()
    },
    isSelected(user) {
      return this.selectedIds.includes(String(user._id))
    },
    toggleUser(user, checked) {
      const id = String(user._id)
      if (checked) {
        if (!this.selectedIds.includes(id)) this.selectedIds.push(id)
      } else {
        this.selectedIds = this.selectedIds.filter(x => x !== id)
      }
    },
    toggleAllFiltered(checked) {
      if (checked) {
        const ids = new Set(this.selectedIds)
        for (const u of this.filteredUsers) ids.add(String(u._id))
        this.selectedIds = [...ids]
      } else {
        const filteredSet = new Set(this.filteredUsers.map(u => String(u._id)))
        this.selectedIds = this.selectedIds.filter(id => !filteredSet.has(id))
      }
    },
    initials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      const a = parts[0] ? parts[0][0] : ''
      const b = parts[1] ? parts[1][0] : ''
      return (a + b).toUpperCase() || '?'
    },
    loadUsers() {
      this.isLoading = true
      const apiType = this.currentType === 'T' ? 'ST' : 'P'
      this.loadSchoolUsers({ token: this.getTeacherToken, type: apiType })
        .finally(() => { this.isLoading = false })
    },
        proceed() {
      if (!this.selectedIds.length) return
      // Backend takes emails; resolve from selected user objects
      const idSet = new Set(this.selectedIds)
      const emails = this.sourceUsers
        .filter(u => idSet.has(String(u._id)))
        .map(u => u.email)
        .filter(Boolean)

      if (!emails.length) {
        this.$buefy.toast.open({ message: 'Selected users do not have email addresses', type: 'is-warning' })
        return
      }

      this.isSubmitting = true
          this.assignClassroom({
            id: this.id,
            token: this.getTeacherToken,
        assign: { type: this.currentType, users: emails }
          })
            .then(() => {
          this.isSubmitting = false
          this.$emit('over')
              this.$buefy.toast.open({
            message: `Assigned ${emails.length} ${this.currentType === 'T' ? 'teacher' : 'student'}${emails.length === 1 ? '' : 's'} successfully`,
            type: 'is-success'
          })
        })
        .catch(err => {
          this.isSubmitting = false
              this.$buefy.toast.open({
            message: (err.response && err.response.data && err.response.data.msg) || 'Could not assign users',
            type: 'is-danger'
          })
        })
    }
  },
  created() {
    this.loadUsers()
  }
}
  </script>

  <style scoped>
.up-modal.modal-card {
  width: min(640px, 95vw);
  max-width: 95vw;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
}

.up-modal-head.modal-card-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.25rem 1.4rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
  position: relative;
  border-radius: 0;
}

.up-modal-icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.15);
}
.up-modal-icon.is-teacher { background: linear-gradient(135deg, #8919e7, #6b38c7); }
.up-modal-icon.is-student { background: linear-gradient(135deg, #31c8ff, #4361ee); }

.up-modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.01em;
}
.up-modal-sub {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0.1rem 0 0;
}

.up-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.05);
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.up-modal-close:hover {
  background: #1f2937;
  color: #ffffff;
}

.up-modal-body.modal-card-body {
  padding: 1.1rem 1.4rem;
  background: #ffffff;
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.tabs {
  display: inline-flex;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
  align-self: flex-start;
}
.tab {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: none;
  background: transparent;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  padding: 0.45rem 0.9rem;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.tab.is-active {
  background: #1f2937;
  color: #ffffff;
}
.tab-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c, #8919e7);
}

.search-bar {
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.search-bar:focus-within {
  border-color: #8919e7;
  box-shadow: 0 0 0 3px rgba(137, 25, 231, 0.12);
}
.search-icon {
  display: inline-flex;
  color: #94a3b8;
  margin-right: 0.5rem;
}
.search-bar input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  color: #1f2937;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: #64748b;
}
.state-emoji { font-size: 1.8rem; }
.state-text { font-size: 0.95rem; }

.spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(137, 25, 231, 0.2);
  border-top-color: #8919e7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
}

.list-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 0.4rem 0.4rem 0.5rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.selected-info {
  font-size: 0.8rem;
  font-weight: 600;
  color: #8919e7;
  background: rgba(137, 25, 231, 0.1);
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
}

.user-rows {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.check-row,
.user-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  cursor: pointer;
  user-select: none;
}

.user-row {
  padding: 0.65rem 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: #ffffff;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.user-row:hover {
  border-color: #cbd5e1;
}
.user-row.is-selected {
  border-color: #8919e7;
  background: rgba(137, 25, 231, 0.04);
}

.check-row input,
.user-row input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.checkmark {
  display: inline-block;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  border-radius: 5px;
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  position: relative;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.checkmark::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.15s ease;
}
input:checked + .checkmark {
  background: #8919e7;
  border-color: #8919e7;
}
input:checked + .checkmark::after {
  transform: rotate(45deg) scale(1);
}
input:indeterminate + .checkmark {
  background: #8919e7;
  border-color: #8919e7;
}
input:indeterminate + .checkmark::after {
  content: '';
  border: none;
  background: #ffffff;
  width: 8px;
  height: 2px;
  left: 4px;
  top: 7px;
  transform: none;
  border-radius: 1px;
}

.check-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}
.count-pill {
  background: #f1f5f9;
  color: #475569;
  border-radius: 999px;
  font-size: 0.72rem;
  padding: 0.1rem 0.5rem;
}

.user-avatar {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.user-avatar.is-teacher { background: linear-gradient(135deg, #8919e7, #6b38c7); }
.user-avatar.is-student { background: linear-gradient(135deg, #31c8ff, #4361ee); }

.user-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  flex: 1;
}
.user-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-email {
  font-size: 0.8rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.up-modal-foot.modal-card-foot {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 1rem 1.4rem 1.25rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fafbfd;
  border-radius: 0;
}

.up-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.65rem 1.1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.up-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.up-btn-ghost {
  background: transparent;
  color: #475569;
  border: 1px solid rgba(15, 23, 42, 0.1);
}
.up-btn-ghost:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #1f2937;
}
.up-btn-primary {
  background: linear-gradient(135deg, #8919e7, #6b38c7);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(137, 25, 231, 0.28);
}
.up-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(137, 25, 231, 0.36);
}

@media screen and (max-width: 540px) {
  .list-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
  }
  .up-modal-foot {
    flex-direction: column-reverse;
  }
  .up-btn {
    width: 100%;
  }
  }
  </style>
